import { API } from '../config'
import { AxiosResponse } from 'axios'
import { PregnancyDiagnosisParams } from 'src/domain/coverage-process'
import { PregnancyDiagnosisDto } from 'src/dto/pregnancy-diagnosis.dto'
import { CreateAnimalForm } from 'src/@types/validations/Animal'
import { CoverageProcessParams } from 'src/domain/coverage-process'
import { CoverageProcessDto } from 'src/dto/coverage-process.dto'
import { IAnimal, UpdateAnimalDto } from 'src/dto/animal.dto'
import { MontaTypes } from 'src/dto/couple-planning.dto'
import { AnimalMovementService } from './animal-movement.service'
import { AnimalController } from 'src/controllers/animal.controller'

const BASE_URL = '/entity/AnimalDiagGest'

export interface SaveDiagnosisWithBirthParams extends PregnancyDiagnosisParams {
  nascimento?: CreateAnimalForm[] | null
  dataParto?: string | null
  tipoParto?: 'normal' | 'cesariana'
  previsaoParto?: string | null
  tempoGestacao?: number | null
}

export interface SaveDiagnosisCompleteParams {
  diagnosis: SaveDiagnosisWithBirthParams
  coverageProcess?: CoverageProcessParams
  updateProcess?: boolean
  motherData?: {
    idLoteAtual?: string | null
    idPastoAtual?: string | null
    nome?: string | null
  }
  username: string
}

export interface SaveDiagnosisCompleteInput {
  diagnosis: SaveDiagnosisWithBirthParams
  coverageProcess?: CoverageProcessParams
  updateProcess?: boolean
  motherData?: {
    idLoteAtual?: string | null
    idPastoAtual?: string | null
    nome?: string | null
  }
}

/**
 * Serviço centralizado para gerenciamento de diagnósticos de prenhez.
 * Segue arquitetura em camadas, orquestrando:
 * - Criação/atualização de diagnósticos
 * - Criação de animais (filhotes) quando aplicável
 * - Atualização de processos de cobertura
 */
export class PregnancyDiagnosisService {
  private animalMovementService: AnimalMovementService
  private animalController: AnimalController

  constructor() {
    this.animalMovementService = new AnimalMovementService()
    this.animalController = new AnimalController()
  }

  // ========== Métodos de Leitura ==========

  /**
   * Busca lista de diagnósticos por fazenda
   */
  public async getPregnancyDiagnosisListByFarmId(farmId: string): Promise<AxiosResponse<PregnancyDiagnosisDto[]>> {
    return await API.get(`${BASE_URL}/${farmId}/null`)
  }

  /**
   * Busca lista de diagnósticos por animal
   */
  public async getPregnancyDiagnosisListByAnimalId(
    farmId: string,
    animalId: string
  ): Promise<AxiosResponse<PregnancyDiagnosisDto[]>> {
    return await API.get(`${BASE_URL}/${farmId}/${animalId}`)
  }

  /**
   * Busca diagnóstico específico por ID
   */
  public async getPregnancyDiagnosisById(farmId: string, id: string): Promise<AxiosResponse<PregnancyDiagnosisDto>> {
    const encodedId = encodeURIComponent(farmId + id)
    return await API.get(`/entity/idHashRange/AnimalDiagGest/${encodedId}/getId`)
  }

  // ========== Métodos de Escrita Básicos ==========

  /**
   * Cria um novo diagnóstico de prenhez
   */
  public async createPregnancyDiagnosis(
    farmId: string,
    pregnancyDiagnosisDto: PregnancyDiagnosisParams
  ): Promise<AxiosResponse<PregnancyDiagnosisDto>> {
    return await API.put(`/entity/AnimalDiagGest/${farmId}`, pregnancyDiagnosisDto)
  }

  /**
   * Atualiza um diagnóstico de prenhez existente
   */
  public async updatePregnancyDiagnosis(
    farmId: string,
    pregnancyDiagnosisDto: PregnancyDiagnosisParams
  ): Promise<AxiosResponse<PregnancyDiagnosisDto>> {
    return await API.put(`/entity/AnimalDiagGest/${farmId}`, pregnancyDiagnosisDto)
  }

  // ========== Métodos de Camada de Serviço (Orquestração) ==========

  /**
   * Cria animais (filhotes) resultantes de um parto
   * @private - Método auxiliar para ser usado internamente
   */
  private async createBirthAnimals(
    farmId: string,
    animals: CreateAnimalForm[],
    username: string,
    motherData?: { idLoteAtual?: string | null; idPastoAtual?: string | null; nome?: string | null },
    dataParto?: string | null
  ): Promise<IAnimal[]> {
    const animalPromises = animals.map(async (animal: UpdateAnimalDto) => {
      // Criar animal completo (animal + produtivo + histórico) usando o mesmo fluxo da tela de criação
      const result = await this.animalController.createAnimalComplete(farmId, username, {
        ...animal,
        idFazenda: farmId,
        dataNascimento: dataParto,
        acao: 'Atualizacao',
        idLoteAtual: animal.idLoteAtual || motherData?.idLoteAtual,
        idPastoAtual: animal.idPastoAtual || motherData?.idPastoAtual
      })

      const createdAnimal = result.animal
      const animalId = createdAnimal.idAnimal || createdAnimal.id || ''

      if (animal.imagem && animalId) {
        await this.animalController.updateAnimalImage({
          farmId,
          animalId,
          imageBase64: animal.imagem
        })
      }

      // // Criar movimentação de nascimento para o animal usando o service
      // await this.animalMovementService.createBirthMovement({
      //   farmId,
      //   animalId: animalId,
      //   animalName: createdAnimal.nome || '',
      //   loteDestino: motherData?.idLoteAtual,
      //   pastoDestino: motherData?.idPastoAtual,
      //   username
      // })

      return createdAnimal
    })

    return await Promise.all(animalPromises)
  }

  /**
   * Atualiza o processo de cobertura relacionado ao diagnóstico
   * @private - Método auxiliar para ser usado internamente
   */
  private async updateRelatedCoverageProcess(
    farmId: string,
    coverageProcess: CoverageProcessParams
  ): Promise<AxiosResponse<CoverageProcessDto>> {
    return await API.put(`/entity/ProcessoCobertura/${farmId}`, coverageProcess)
  }

  /**
   * Cria um processo de cobertura
   * @private - Método auxiliar para ser usado internamente
   */
  private async createCoverageProcess(
    farmId: string,
    coverageProcess: CoverageProcessParams
  ): Promise<AxiosResponse<CoverageProcessDto>> {
    return await API.put(`/entity/ProcessoCobertura/${farmId}`, coverageProcess)
  }

  /**
   * Atualiza a data de nascimento dos animais existentes
   * @private - Método auxiliar para ser usado internamente
   */
  private async updateBirthDateOfAnimals(
    farmId: string,
    animalIds: string[],
    newBirthDate: string | null
  ): Promise<void> {
    if (!animalIds || animalIds.length === 0 || !newBirthDate) return

    try {
      const updatePromises = animalIds.map(async (idAnimal) => {
        const animal = await this.animalController.getAnimalById(farmId, idAnimal)
        await this.animalController.updateAnimal(farmId, {
          ...animal,
          dataNascimento: newBirthDate,
          acao: 'Atualizacao'
        })
      })

      await Promise.all(updatePromises)
    } catch (error) {
      console.error('Erro ao atualizar data de nascimento dos animais:', error)
      throw error
    }
  }

  /**
   * Salva diagnóstico completo com todas as operações relacionadas
   * Este método orquestra:
   * 1. Criação/atualização do diagnóstico
   * 2. Criação de animais (filhotes) se diagnóstico for prenha e houver nascimentos
   * 3. Criação de movimentação de nascimento para cada filhote
   * 4. Atualização do processo de cobertura se necessário
   *
   * @param farmId - ID da fazenda
   * @param params - Parâmetros completos incluindo diagnóstico, nascimentos, processo e dados da mãe
   * @returns Diagnóstico salvo e IDs dos animais criados
   */
  public async saveDiagnosisComplete(
    farmId: string,
    params: SaveDiagnosisCompleteParams
  ): Promise<{
    diagnosis: PregnancyDiagnosisDto
    createdAnimals?: IAnimal[]
    updatedProcess?: CoverageProcessDto
  }> {
    const { diagnosis, coverageProcess, updateProcess = false, motherData, username } = params

    try {
      let updatedProcess: CoverageProcessDto | undefined
      if (updateProcess && coverageProcess) {
        const processResponse = await this.updateRelatedCoverageProcess(farmId, coverageProcess)
        updatedProcess = processResponse.data
      }

      let existingDiagnosis: PregnancyDiagnosisDto | undefined
      if (diagnosis.idDiagGest) {
        try {
          const compositeId = `#${diagnosis.idAnimal}/${diagnosis.idDiagGest}`
          const existingResponse = await this.getPregnancyDiagnosisById(diagnosis.idFazenda, compositeId)
          existingDiagnosis = existingResponse.data
        } catch (error) {
          console.warn('Não foi possível buscar diagnóstico existente:', error)
        }
      }

      // 3. Criar novos animais (filhotes) se o diagnóstico for "prenha" e houver nascimentos
      let createdAnimals: IAnimal[] | undefined
      let animalIds: string[] = []

      if (diagnosis.diagnostico === 'prenha' && diagnosis.nascimento) {
        // Converter para array se necessário e filtrar apenas objetos válidos
        const nascimentoArray = Array.isArray(diagnosis.nascimento)
          ? diagnosis.nascimento
          : Object.values(diagnosis.nascimento).filter((item: any) => item?.idFazenda)

        if (nascimentoArray.length > 0) {
          createdAnimals = await this.createBirthAnimals(
            farmId,
            nascimentoArray as CreateAnimalForm[],
            username,
            motherData,
            diagnosis.dataParto
          )
          animalIds = createdAnimals.map((animal) => animal.idAnimal || animal.id || '')
        }
      }

      // 4. Se for update e já existem filhotes, atualizar data de nascimento se mudou
      if (existingDiagnosis && existingDiagnosis.idCria && existingDiagnosis.idCria.length > 0) {
        if (diagnosis.dataParto && diagnosis.dataParto !== existingDiagnosis.dataParto) {
          await this.updateBirthDateOfAnimals(diagnosis.idFazenda, existingDiagnosis.idCria, diagnosis.dataParto)
        }
        // Manter os IDs dos filhotes existentes se não criou novos
        if (animalIds.length === 0) {
          animalIds = existingDiagnosis.idCria
        }
      }

      // 5. Preparar payload do diagnóstico com os IDs dos animais
      const diagnosisPayload: PregnancyDiagnosisParams = {
        idFazenda: diagnosis.idFazenda,
        idAnimal: diagnosis.idAnimal,
        touro: diagnosis.touro,
        idDiagGest: diagnosis.idDiagGest,
        tipoReproducao: diagnosis.tipoReproducao,
        dataInicial: diagnosis.dataInicial,
        dataCobertura: diagnosis.dataCobertura,
        dataDiagnostico: diagnosis.dataDiagnostico,
        dataParto: diagnosis.dataParto,
        diagnostico: diagnosis.diagnostico,
        inseminador: diagnosis.inseminador,
        doses: diagnosis.doses,
        idCria: animalIds.length > 0 ? animalIds : undefined,
        acao: diagnosis.acao || 'Atualizacao',
        status: diagnosis.status,
        idTransacao: diagnosis.idTransacao
      }

      // 6. Salvar/atualizar o diagnóstico
      const diagnosisResponse = diagnosis.idDiagGest
        ? await this.updatePregnancyDiagnosis(farmId, diagnosisPayload)
        : await this.createPregnancyDiagnosis(farmId, diagnosisPayload)

      return {
        diagnosis: diagnosisResponse.data,
        createdAnimals,
        updatedProcess
      }
    } catch (error) {
      console.error('Erro ao salvar diagnóstico completo:', error)
      throw error
    }
  }

  /**
   * Atualiza apenas o diagnóstico com informações de nascimento
   * Método simplificado para quando só precisa atualizar o diagnóstico e criar filhotes
   *
   * @param farmId - ID da fazenda
   * @param diagnosis - Dados do diagnóstico incluindo nascimentos
   * @param username - Nome do usuário para registro de movimentações
   * @returns Diagnóstico atualizado e animais criados
   */
  public async updateDiagnosisWithBirth(
    farmId: string,
    diagnosis: SaveDiagnosisWithBirthParams,
    username: string
  ): Promise<{
    diagnosis: PregnancyDiagnosisDto
    createdAnimals?: IAnimal[]
  }> {
    const result = await this.saveDiagnosisComplete(farmId, {
      diagnosis,
      updateProcess: false,
      username
    })

    return {
      diagnosis: result.diagnosis,
      createdAnimals: result.createdAnimals
    }
  }

  // ========== Métodos de Criação de Casais (Diagnóstico + Processo) ==========

  /**
   * Cria um novo casal (diagnóstico + processo de cobertura)
   * Este método orquestra:
   * 1. Criação do diagnóstico
   * 2. Criação do processo de cobertura vinculado ao diagnóstico
   *
   * @param farmId - ID da fazenda
   * @param diagnosisData - Dados do diagnóstico
   * @param coverageData - Dados do processo de cobertura
   * @returns Diagnóstico e processo criados
   */
  public async createCouple(
    farmId: string,
    diagnosisData: PregnancyDiagnosisParams,
    coverageData: CoverageProcessParams
  ): Promise<{
    diagnosis: PregnancyDiagnosisDto
    coverageProcess: CoverageProcessDto
  }> {
    try {
      // 1. Criar o diagnóstico primeiro
      const diagnosisResponse = await this.createPregnancyDiagnosis(farmId, diagnosisData)
      const createdDiagnosis = diagnosisResponse.data

      // 2. Criar o processo de cobertura vinculado ao diagnóstico
      const coveragePayload: CoverageProcessParams = {
        ...coverageData,
        idDiagGest: createdDiagnosis.idDiagGest
      }

      const coverageResponse = await this.createCoverageProcess(farmId, coveragePayload)

      return {
        diagnosis: createdDiagnosis,
        coverageProcess: coverageResponse.data
      }
    } catch (error) {
      console.error('Erro ao criar casal:', error)
      throw error
    }
  }

  /**
   * Cria múltiplos casais (diagnóstico + processo de cobertura) em lote
   * Usado principalmente para reprodução tipo "monta" com lote de animais
   *
   * @param farmId - ID da fazenda
   * @param animals - Lista de animais para criar casais
   * @param commonData - Dados comuns para todos os casais (touro, datas, etc)
   * @returns Lista de casais criados
   */
  public async createMultipleCouples(
    farmId: string,
    animals: IAnimal[],
    commonData: {
      idTouro: string
      dataInicio: string
      dataFim?: string | null
      tipoMonta: MontaTypes
      inseminador?: string
      dosagem?: string
    }
  ): Promise<
    Array<{
      animal: IAnimal
      diagnosis: PregnancyDiagnosisDto
      coverageProcess: CoverageProcessDto
    }>
  > {
    const results: Array<{
      animal: IAnimal
      diagnosis: PregnancyDiagnosisDto
      coverageProcess: CoverageProcessDto
    }> = []

    for (const animal of animals) {
      try {
        // Criar diagnóstico para o animal
        const diagnosisData: PregnancyDiagnosisParams = {
          idFazenda: farmId,
          idAnimal: animal.idAnimal || '',
          touro: commonData.idTouro,
          tipoReproducao: commonData.tipoMonta,
          dataInicial: commonData.dataInicio,
          dataCobertura: commonData.dataInicio, // Data da cobertura (importante para cálculo de gestação)
          dataDiagnostico: null, // Será preenchido quando fizer o diagnóstico
          diagnostico: null, // Status será definido no diagnóstico posterior
          inseminador: commonData.inseminador || '',
          doses: commonData.dosagem || '',
          acao: 'Atualizacao',
          status: 'emProcesso'
        }

        // Criar processo de cobertura para o animal
        const coverageData: CoverageProcessParams = {
          idFazenda: farmId,
          idVaca: animal.idAnimal || '',
          idTouro: commonData.idTouro,
          dataInicio: commonData.dataInicio,
          dataFim: commonData.dataFim || null,
          tipoMonta: commonData.tipoMonta,
          inseminador: commonData.inseminador || '',
          dosagem: commonData.dosagem || '',
          dataCio: commonData.dataInicio,
          idDiagGest: '',
          acao: 'Atualizacao'
        }

        const result = await this.createCouple(farmId, diagnosisData, coverageData)

        results.push({
          animal,
          diagnosis: result.diagnosis,
          coverageProcess: result.coverageProcess
        })
      } catch (error) {
        console.error(`Erro ao criar casal para animal ${animal.nome || animal.idAnimal}:`, error)
        // Continua processando os demais animais mesmo se um falhar
      }
    }

    return results
  }
}
